import type { ConnectionPhase } from '@parsleyai/ui'
import {
  collection,
  type DocumentData,
  deleteDoc,
  deleteField,
  doc,
  onSnapshot,
  type QuerySnapshot,
  serverTimestamp,
  setDoc,
  type Timestamp,
  updateDoc
} from 'firebase/firestore'
import { createContext, type ReactNode, useCallback, useEffect, useReducer } from 'react'
import { db } from '@/services/firebase/config'
import { useLiveSubscription } from '@/store/userData/useLiveSubscription'
import { useUserData } from '@/store/userData/useUserData'
import type { FoodProfile } from '@/types/foodProfile'
import type { IngredientRule, RuleData } from '@/types/ingredientRules'
import { foodProfilesReducer, initialFoodProfilesState } from './foodProfilesReducer'
import { FoodProfilesActionTypes, type FoodProfilesState } from './types'

type UpdateFoodProfileData = Partial<Omit<FoodProfile, 'id' | 'createdAt' | 'updatedAt'>>

export interface FoodProfilesContextValue {
  state: FoodProfilesState
  createFoodProfile: (data: { name: string; ingredientRules: Record<string, IngredientRule> }) => Promise<string>
  updateFoodProfile: (id: string, data: UpdateFoodProfileData) => Promise<void>
  deleteFoodProfile: (id: string) => Promise<void>
  toggleFoodProfileActive: (id: string, isActive: boolean) => Promise<void>
  addIngredientRule: (profileId: string, ruleId: string, data: RuleData) => Promise<void>
  updateIngredientRule: (profileId: string, ruleId: string, data: RuleData, createdAt: Timestamp) => Promise<void>
  deleteIngredientRule: (profileId: string, ruleId: string) => Promise<void>
  /** Live-connection phase for the subscription (auto-resubscribes on drop; banner-ready). */
  phase: ConnectionPhase
  /** Manual resubscribe. */
  retry: () => void
}

export const FoodProfilesContext = createContext<FoodProfilesContextValue | undefined>(undefined)

interface FoodProfilesProviderProps {
  children: ReactNode
}

export function FoodProfilesProvider({ children }: FoodProfilesProviderProps) {
  const { uid } = useUserData()
  const [state, dispatch] = useReducer(foodProfilesReducer, initialFoodProfilesState)

  // Drop cached profiles on sign-out; the subscription itself is disabled by the null key below.
  useEffect(() => {
    if (!uid) dispatch({ type: FoodProfilesActionTypes.CLEAR })
  }, [uid])

  const { phase, retry } = useLiveSubscription<QuerySnapshot<DocumentData>>({
    subscriptionKey: uid,
    label: 'foodProfiles',
    subscribe: (activeUid, { onData, onError }) => onSnapshot(collection(db, 'users', activeUid, 'food_profiles'), onData, onError),
    onData: (snapshot) => {
      const foodProfiles = snapshot.docs.map((d) => ({
        id: d.id,
        ...d.data()
      })) as FoodProfilesState['foodProfiles']
      dispatch({ type: FoodProfilesActionTypes.SET_FOOD_PROFILES, payload: foodProfiles })
    }
  })

  const createFoodProfile = useCallback(
    async (data: { name: string; ingredientRules: Record<string, IngredientRule> }): Promise<string> => {
      if (!uid) return ''
      const docRef = doc(collection(db, 'users', uid, 'food_profiles'))
      await setDoc(docRef, {
        id: docRef.id,
        name: data.name,
        isActive: true,
        templateId: null,
        ingredientRules: data.ingredientRules,
        createdAt: serverTimestamp()
      })
      return docRef.id
    },
    [uid]
  )

  const updateFoodProfile = useCallback(
    async (id: string, data: UpdateFoodProfileData) => {
      if (!uid) return
      await updateDoc(doc(db, 'users', uid, 'food_profiles', id), {
        ...data,
        updatedAt: serverTimestamp()
      })
    },
    [uid]
  )

  const deleteFoodProfile = useCallback(
    async (id: string) => {
      if (!uid) return
      // Rules are a map field on the profile doc — they're deleted automatically.
      await deleteDoc(doc(db, 'users', uid, 'food_profiles', id))
    },
    [uid]
  )

  const toggleFoodProfileActive = useCallback(
    async (id: string, isActive: boolean) => {
      if (!uid) return
      await updateDoc(doc(db, 'users', uid, 'food_profiles', id), {
        isActive,
        updatedAt: serverTimestamp()
      })
    },
    [uid]
  )

  const addIngredientRule = useCallback(
    async (profileId: string, ruleId: string, data: RuleData) => {
      if (!uid) return
      await updateDoc(doc(db, 'users', uid, 'food_profiles', profileId), {
        [`ingredientRules.${ruleId}`]: {
          id: ruleId,
          ingredient: data.ingredient,
          excludedMembers: data.excludedMembers ?? [],
          substitutions: data.substitutions ?? [],
          ...(data.note ? { note: data.note } : {}),
          createdAt: serverTimestamp()
        }
      })
    },
    [uid]
  )

  const updateIngredientRule = useCallback(
    async (profileId: string, ruleId: string, data: RuleData, createdAt: Timestamp) => {
      if (!uid) return
      await updateDoc(doc(db, 'users', uid, 'food_profiles', profileId), {
        [`ingredientRules.${ruleId}`]: {
          id: ruleId,
          ingredient: data.ingredient,
          excludedMembers: data.excludedMembers ?? [],
          substitutions: data.substitutions ?? [],
          ...(data.note ? { note: data.note } : {}),
          createdAt,
          updatedAt: serverTimestamp()
        }
      })
    },
    [uid]
  )

  const deleteIngredientRule = useCallback(
    async (profileId: string, ruleId: string) => {
      if (!uid) return
      await updateDoc(doc(db, 'users', uid, 'food_profiles', profileId), {
        [`ingredientRules.${ruleId}`]: deleteField()
      })
    },
    [uid]
  )

  return (
    <FoodProfilesContext.Provider
      value={{
        state,
        createFoodProfile,
        updateFoodProfile,
        deleteFoodProfile,
        toggleFoodProfileActive,
        addIngredientRule,
        updateIngredientRule,
        deleteIngredientRule,
        phase,
        retry
      }}>
      {children}
    </FoodProfilesContext.Provider>
  )
}
